import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import useSeoMeta from '../hooks/useSeoMeta';
import { requestPasswordReset } from '../api/passwordReset';
import { extractApiErrorMessage } from '../utils/apiError';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useSeoMeta(
    'Forgot Password — Pinecurl',
    'Reset your Pinecurl account password.'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await requestPasswordReset({ email });
      setIsSubmitted(true);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-app px-4">
      <div className="max-w-md w-full">
        <div className="mb-4">
          <Link
            to="/login"
            className="text-gray-400 hover:text-white text-sm flex items-center gap-1 transition-colors"
          >
            ← Back to sign in
          </Link>
        </div>

        <div className="bg-surface border border-border rounded-xl p-8 shadow-lg">
          {isSubmitted ? (
            /* ── Success state ── */
            <div className="text-center">
              <div
                className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/30 rounded-full flex items-center justify-center mx-auto mb-4"
                aria-hidden="true"
              >
                <svg
                  className="w-7 h-7 text-emerald-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
              </div>
              <h1 className="text-xl font-bold text-text-primary mb-2">Check your email</h1>
              <p className="text-text-secondary text-sm leading-relaxed mb-6">
                If <span className="text-text-primary font-medium">{email}</span> is
                registered, we've sent a password reset link. It expires in&nbsp;1&nbsp;hour.
              </p>
              <p className="text-text-muted text-xs">
                Didn't receive it?{' '}
                <button
                  type="button"
                  className="text-primary-400 hover:text-primary-300 underline"
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                  }}
                >
                  Try again
                </button>{' '}
                or check your spam folder.
              </p>
            </div>
          ) : (
            /* ── Request form ── */
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-text-primary">Forgot password?</h1>
                <p className="text-text-secondary text-sm mt-2">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-accent-red px-4 py-3 rounded-lg mb-6 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <Input
                  label="Email address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                />

                <Button type="submit" className="w-full mt-2" isLoading={isLoading}>
                  Send reset link
                </Button>
              </form>

              <p className="text-center text-text-secondary text-sm mt-6">
                Remembered it?{' '}
                <Link to="/login" className="text-primary-400 hover:text-primary-300">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
